import { ChevronDown, X } from "lucide-react";
import React, { useEffect, useState } from "react";
import PrimaryBtn from "../primaryBtn";
import { CURRENCY } from "src/lib/constans";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faXmark } from "@fortawesome/free-solid-svg-icons";

interface IProps {
  data?: { min: number; max: number } | null;
  setData: (data: { min: number; max: number } | null) => void;
  labelText?: string;
  isRequired?: boolean;
  extraClass?: string;
  placeholder?: string;
  dropdownMainClass?: string;
  dropdownMainId?: string;
  newUI?: boolean;
  loading?: boolean;
  clearId?: string;
  dropdownInnerStyle?: React.CSSProperties;
  disabled?: boolean;
  error?: string;
  dropdownInnerClass?: string;
  dropdownPosition?: "top" | "bottom";
  mainDivClass?: string;
  withoutMargin?: boolean;
  priceRange?: { min: number; max: number };
}

const PriceFilterDropdown = ({
  data = null,
  setData = () => {},
  labelText = "",
  isRequired,
  extraClass,
  placeholder,
  dropdownMainClass,
  dropdownMainId,
  newUI,
  loading,
  clearId,
  dropdownInnerStyle = {},
  disabled,
  error,
  dropdownInnerClass,
  dropdownPosition = "bottom",
  mainDivClass,
  withoutMargin,
  priceRange = { min: 0, max: 10000 },
}: IProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [min, setMin] = useState<string>(priceRange?.min?.toString() || "");
  const [max, setMax] = useState<string>(priceRange?.max?.toString() || "");

  let dropdownPositionClass =
    dropdownPosition === "top"
      ? "bottom-full 2xl:mb-1 mb-0.5"
      : "top-full 2xl:mt-1 mt-0.5";

  useEffect(() => {
    const windowClick = (e: MouseEvent) => {
      setIsOpen(false);
    };
    window.addEventListener("click", windowClick);
    return () => window.removeEventListener("click", windowClick);
  }, []);

  return (
    <div className={`w-full ${mainDivClass || ""}`}>
      {labelText && (
        <label
          className={`label-color-select-city ${
            newUI
              ? "font-Poppins-Medium text-[#2f2f31] font-size-16px"
              : "font-Poppins-Regular font-size-18px"
          }`}
        >
          {isRequired && <span className="text-[#FF0000]">* </span>}
          {labelText}
        </label>
      )}
      <div
        className={`w-full relative select-none text-[#2F2F31] font-Poppins-Regular font-size-16px  ${
          withoutMargin ? "" : "mb-[1rem] input-field-margin-top"
        } ${extraClass || ""}`}
      >
        <button
          disabled={loading || disabled}
          className={`w-full flex items-center justify-between rounded-[5px] border border-[#707070] ${
            disabled ? "cursor-not-allowed" : "cursor-pointer"
          } bg-white ${dropdownMainClass || ""} ${
            newUI
              ? "!border !border-[#ACACAC] 2xl:!rounded-[10px] !rounded-[6px] "
              : ""
          }`}
          id={dropdownMainId || ""}
          onClick={(e) => {
            e.stopPropagation();
            if (loading) return;
            setIsOpen(!isOpen);
          }}
          type="button"
        >
          <div
            className={`p-1.5 2xl:p-2 ${
              !loading
                ? "w-[calc(100%-30px)]"
                : "w-[calc(100%-60px)]"
            } ${dropdownInnerClass || ""}`}
            style={{
              ...dropdownInnerStyle,
            }}
          >
            {data && !loading ? (
              <div className="w-full flex items-center gap-1 truncate text-left h-full">
                {`${data.min || 0} - ${data.max || 0}`}
              </div>
            ) : (
              <p className="text-gray-500 h-full flex items-center text-left w-full whitespace-nowrap">
                {placeholder || "Select Option"}
              </p>
            )}
          </div>
          <div className="flex items-center justify-end pr-[0.5rem] w-[60px]">
            {data && !loading ? (
              <FontAwesomeIcon
                icon={faXmark}
                color="gray"
                className="cursor-pointer font-size-16px scale-110"
                onClick={(e) => {
                  e.stopPropagation();
                  setData(null);
                  setIsOpen(false);
                }}
                id={`clear-dropdown-${clearId || ""}`}
              />
            ) : null}
            <FontAwesomeIcon
              icon={faChevronDown}
              color="gray"
              className={`${isOpen ? "scale-[-1]" : ""} font-size-15px transition-all`}
              onClick={(e) => {
                e.stopPropagation();
                setIsOpen(!isOpen);
              }}
              id="dropdown-icon"
            />
          </div>
        </button>
        {isOpen && (
          <div
            className={`w-full p-2 flex flex-col gap-2 absolute shadow-md overflow-hidden ${dropdownPositionClass} left-0 z-[1] bg-white rounded-[5px] border border-[#E0E0E0] max-h-[200px] overflow-y-auto scrollbar-5`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-2 justify-between">
              <p>Min</p>
              <div className="w-[100px] flex items-center font-size-12px gap-1 border border-[#00443f] rounded-[2px] px-2 py-0.5">
                <span className="text-[#00443f]">{CURRENCY}</span>
                <input
                  type="number"
                  placeholder={priceRange?.min?.toString() || "0"}
                  value={min}
                  onChange={(e) => setMin(e.target.value || "")}
                  className="outline-none w-full"
                />
              </div>
            </div>
            <div className="flex items-center gap-2 justify-between">
              <p>Max</p>
              <div className="w-[100px] flex items-center font-size-12px gap-1 border border-[#00443f] rounded-[2px] px-2 py-0.5">
                <span className="text-[#00443f]">{CURRENCY}</span>
                <input
                  type="number"
                  placeholder={priceRange?.max?.toString() || "0"}
                  value={max}
                  onChange={(e) => setMax(e.target.value || "")}
                  className="outline-none w-full"
                />
              </div>
            </div>
            <PrimaryBtn
              ButtonText="Done"
              onClick={() => {
                setData({
                  min: parseFloat(min),
                  max: parseFloat(max),
                });
                setIsOpen(false);
              }}
              btnExtraStyle={{
                width: "100%",
                margin: "0 auto",
                backgroundColor: "#00443f",
                padding: "0.2rem 1rem",
              }}
            />
          </div>
        )}
      </div>
      {error && (
        <p className="text-red-500 -mt-3 mb-1 font-size-14px font-Poppins-Regular">
          {error}
        </p>
      )}
    </div>
  );
};

export default PriceFilterDropdown;
